import type { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import type { QuoteTaxRepository } from '../../application/QuoteTaxRepository'
import { quoteTaxProfileRowSchema, quoteTaxRateRowSchema, type QuoteTaxRate } from '../../domain/quoteTax'

interface SupabaseResponse {
  data: unknown
  error: { message: string; code?: string } | null
}

function asResponse(value: unknown): SupabaseResponse {
  return value as SupabaseResponse
}

const activationResultSchema = z.object({
  profileId: z.uuid(),
  version: z.number().int().positive(),
  status: z.enum(['ACTIVE', 'RETIRED']),
  activatedAt: z.string(),
})

export function createSupabaseQuoteTaxRepository(client: AppSupabaseClient): QuoteTaxRepository {
  const untypedClient = client as unknown as SupabaseClient
  return {
    async listProfiles() {
      const [profileResponse, rateResponse] = await Promise.all([
        untypedClient
          .from('quote_tax_profiles')
          .select('id,version,label,status,rounding_strategy,activated_at')
          .order('version', { ascending: false }),
        untypedClient
          .from('quote_tax_rates')
          .select('id,profile_id,code,label,rate_ppm,calculation_base,display_order')
          .order('display_order', { ascending: true }),
      ])
      const profiles = asResponse(profileResponse)
      const rates = asResponse(rateResponse)
      if (profiles.error) throw new Error(`QUOTE_TAX_PROFILES_FAILED:${profiles.error.code ?? 'UNKNOWN'}`)
      if (rates.error) throw new Error(`QUOTE_TAX_RATES_FAILED:${rates.error.code ?? 'UNKNOWN'}`)
      const parsedProfiles = z.array(quoteTaxProfileRowSchema).safeParse(profiles.data)
      const parsedRates = z.array(quoteTaxRateRowSchema).safeParse(rates.data)
      if (!parsedProfiles.success || !parsedRates.success)
        throw new Error('QUOTE_TAX_PROFILE_RESPONSE_INVALID')
      const ratesByProfile = new Map<string, QuoteTaxRate[]>()
      for (const rate of parsedRates.data) {
        const mapped: QuoteTaxRate = {
          id: rate.id,
          code: rate.code,
          label: rate.label,
          ratePpm: rate.rate_ppm,
          calculationBase: rate.calculation_base,
          displayOrder: rate.display_order,
        }
        ratesByProfile.set(rate.profile_id, [...(ratesByProfile.get(rate.profile_id) ?? []), mapped])
      }
      return {
        profiles: parsedProfiles.data.map((profile) => ({
          id: profile.id,
          version: profile.version,
          label: profile.label,
          status: profile.status,
          ...(profile.rounding_strategy ? { roundingStrategy: profile.rounding_strategy } : {}),
          activatedAt: profile.activated_at,
          rates: ratesByProfile.get(profile.id) ?? [],
        })),
        meta: { source: 'supabase' as const, retrievedAt: new Date().toISOString() },
      }
    },
    async activateProfile(command) {
      const response = asResponse(
        await untypedClient.rpc('activate_quote_tax_profile_v2', {
          p_command: {
            idempotencyKey: command.idempotencyKey,
            label: command.label,
            roundingStrategy: command.roundingStrategy,
            rates: command.rates,
          },
        }),
      )
      if (response.error)
        throw new Error(
          response.error.message || `QUOTE_TAX_PROFILE_ACTIVATION_FAILED:${response.error.code ?? 'UNKNOWN'}`,
        )
      const parsed = activationResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('QUOTE_TAX_PROFILE_ACTIVATION_RESPONSE_INVALID')
      return parsed.data
    },
  }
}
